import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  LayoutGrid, Users, Route, CalendarCheck, Receipt, Settings,
  PanelLeftClose, PanelLeftOpen, LogOut, Menu, X,
} from "lucide-react";
import { OrganizationContext } from "../clients/utils/OrganizationContext";
import OrgDashboard    from "./components/OrgDashboard";
import OrgMembers      from "./components/OrgMembers";
import OrgMissions     from "./components/OrgMissions";
import OrgReservations from "./components/OrgReservations";
import OrgStatements   from "./components/OrgStatements";
import OrgSettings     from "./components/OrgSettings";

const API = "https://api.komorotel.com/api";

const PAGES = [
  { key: "dashboard",    label: "Aperçu",       icon: LayoutGrid },
  { key: "members",      label: "Membres",      icon: Users },
  { key: "missions",     label: "Missions",     icon: Route },
  { key: "reservations", label: "Réservations", icon: CalendarCheck, badge: true },
  { key: "statements",   label: "Relevés",      icon: Receipt },
  { key: "settings",     label: "Réglages",     icon: Settings },
];

/* La barre est une surface verte pleine, celle du logo. Le compteur sur
   Réservations n'est pas une décoration : c'est la seule chose de cet
   espace qui réclame une action, et il doit se voir même replié. */
export default function Organization() {
  const navigate = useNavigate();
  const { organization } = useContext(OrganizationContext);

  const [page, setPage] = useState("dashboard");
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [aValider, setAValider] = useState(0);

  // Confort par navigateur, sans conséquence s'il n'est pas lisible
  const [replie, setReplie] = useState(() => {
    try { return localStorage.getItem("org-sidebar") === "replie"; } catch { return false; }
  });

  const basculer = () => {
    setReplie(r => {
      try { localStorage.setItem("org-sidebar", r ? "ouvert" : "replie"); } catch {}
      return !r;
    });
  };

  const compter = useCallback(() => {
    axios.get(`${API}/organization/reservations`, {
      params: { pending: true }, withCredentials: true,
    })
      .then(r => setAValider(r.data.length))
      .catch(() => {});
  }, []);

  // Rafraîchi à chaque changement de page : après une validation, le
  // compteur doit tomber sans qu'on recharge.
  useEffect(() => { compter(); }, [compter, page]);

  const rendu = () => {
    switch (page) {
      case "members":      return <OrgMembers />;
      case "missions":     return <OrgMissions />;
      case "reservations": return <OrgReservations />;
      case "statements":   return <OrgStatements />;
      case "settings":     return <OrgSettings />;
      default:             return <OrgDashboard setActivePage={setPage} />;
    }
  };

  const deconnexion = async () => {
    try {
      await fetch(`${API}/organization/logout`, { method: "POST", credentials: "include" });
    } catch (_) {}
    Cookies.remove("org_token", { path: "/", domain: ".komorotel.com" });
    navigate("/organisation/connexion");
  };

  const gestionnaire = organization?.managers?.find(m => m._id === organization?.currentManagerId);
  const initiales = gestionnaire
    ? `${gestionnaire.firstName?.[0] || ""}${gestionnaire.lastName?.[0] || ""}`.toUpperCase()
    : "··";

  const Lien = ({ p, compact }) => {
    const Icon = p.icon;
    const actif = page === p.key;
    const n = p.badge ? aValider : 0;
    return (
      <button
        onClick={() => { setPage(p.key); setMenuOuvert(false); }}
        title={compact ? p.label : undefined}
        aria-current={actif ? "page" : undefined}
        className={`relative flex items-center rounded-lg text-[14px] transition w-full
          ${compact ? "justify-center px-0 py-2" : "gap-2.5 px-3 py-2"}
          ${actif ? "text-white" : "text-white/55 hover:text-white hover:bg-white/5"}`}
        style={actif ? { background: "var(--org-moss)" } : undefined}
      >
        <Icon size={17} strokeWidth={1.75} className="shrink-0" />
        {!compact && <span className="truncate">{p.label}</span>}

        {n > 0 && (
          compact ? (
            <span
              className="absolute top-1.5 right-2.5 w-[7px] h-[7px] rounded-full"
              style={{ background: "var(--org-sun)" }}
            />
          ) : (
            <span
              className="org-num ml-auto text-[12px] font-semibold px-1.5 py-0.5 rounded-md shrink-0"
              style={{ background: "var(--org-sun)", color: "#3B2600" }}
            >
              {n}
            </span>
          )
        )}
      </button>
    );
  };

  const largeur = replie ? "w-[64px]" : "w-[216px]";

  return (
    <div className="org-shell flex h-screen overflow-hidden" style={{ background: "var(--org-paper)" }}>

      {/* ── Barre latérale ─────────────────────────────── */}
      <aside
        className={`hidden md:flex ${largeur} shrink-0 flex-col transition-[width] duration-200`}
        style={{ background: "var(--org-forest)" }}
      >
        <div className={`pt-5 pb-6 ${replie ? "px-3" : "px-4"}`}>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img
                src="/logos/komorotel-icon-blanc.svg"
                alt="Komorotel"
                draggable={false}
                className="shrink-0"
                style={{ height: 22, width: "auto" }}
              />
              {!replie && (
                <span className="text-white text-[15px] font-semibold truncate">Komorotel</span>
              )}
            </div>
            {!replie && (
              <button
                onClick={basculer}
                title="Réduire le menu"
                aria-label="Réduire le menu"
                className="shrink-0 w-7 h-7 -mr-1 flex items-center justify-center rounded-md text-white/35 hover:text-white/80 hover:bg-white/5 transition"
              >
                <PanelLeftClose size={16} strokeWidth={1.75} />
              </button>
            )}
          </div>
          {!replie && (
            <p className="text-white/40 text-[12.5px] mt-2">Espace organisation</p>
          )}
          {replie && (
            <button
              onClick={basculer}
              title="Déployer le menu"
              aria-label="Déployer le menu"
              className="w-full flex justify-center mt-3 text-white/35 hover:text-white/80 transition"
            >
              <PanelLeftOpen size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>

        <nav className={`flex-1 flex flex-col gap-0.5 ${replie ? "px-2.5" : "px-3"}`}>
          {PAGES.map(p => <Lien key={p.key} p={p} compact={replie} />)}
        </nav>

        <div
          className={`py-3.5 ${replie ? "px-2.5" : "px-4"}`}
          style={{ borderTop: "1px solid rgb(255 255 255 / 0.08)" }}
        >
          <div className={`flex items-center gap-2.5 ${replie ? "justify-center" : ""}`}>
            <div
              className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[11px] font-semibold"
              style={{ background: "var(--org-moss)" }}
              title={replie && gestionnaire ? `${gestionnaire.firstName} ${gestionnaire.lastName}` : undefined}
            >
              {initiales}
            </div>
            {!replie && (
              <div className="min-w-0 leading-tight">
                <p className="text-white/85 text-[13px] truncate">
                  {gestionnaire ? `${gestionnaire.firstName} ${gestionnaire.lastName}` : "Gestionnaire"}
                </p>
                <p className="text-white/40 text-[11.5px] truncate mt-[1px]">
                  {organization?.canBook ? "Compte actif" : "Activation en attente"}
                </p>
              </div>
            )}
          </div>

          <button
            onClick={deconnexion}
            title={replie ? "Se déconnecter" : undefined}
            className={`flex items-center gap-2 text-white/40 hover:text-white text-[13px] transition mt-2.5
              ${replie ? "w-full justify-center" : "px-0.5"}`}
          >
            <LogOut size={15} strokeWidth={1.75} />
            {!replie && <span>Se déconnecter</span>}
          </button>
        </div>
      </aside>

      {/* ── Barre mobile ───────────────────────────────── */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40" style={{ background: "var(--org-forest)" }}>
        <div className="flex items-center justify-between px-4 h-[52px]">
          <div className="flex items-baseline gap-2.5 min-w-0">
            <img
              src="/logos/komorotel-icon-blanc.svg"
              alt="Komorotel"
              draggable={false}
              style={{ height: 20, width: "auto", alignSelf: "center" }}
            />
            <span className="text-white/55 text-[15px] truncate">
              {PAGES.find(p => p.key === page)?.label}
            </span>
          </div>
          <button
            onClick={() => setMenuOuvert(o => !o)}
            aria-expanded={menuOuvert}
            aria-label={menuOuvert ? "Fermer le menu" : "Ouvrir le menu"}
            className="relative text-white/80 p-1.5"
          >
            {menuOuvert ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
            {!menuOuvert && aValider > 0 && (
              <span
                className="absolute top-0.5 right-0.5 w-[7px] h-[7px] rounded-full"
                style={{ background: "var(--org-sun)" }}
              />
            )}
          </button>
        </div>
        {menuOuvert && (
          <nav className="px-3 pb-3 flex flex-col gap-1">
            {PAGES.map(p => <Lien key={p.key} p={p} compact={false} />)}
            <div className="h-px mx-3.5 my-1.5" style={{ background: "rgb(255 255 255 / 0.1)" }} />
            <div className="flex items-center gap-3 px-3.5 py-2">
              <div
                className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white text-[12px] font-semibold"
                style={{ background: "var(--org-moss)" }}
              >
                {initiales}
              </div>
              <div className="min-w-0">
                <p className="text-white/85 text-[14px] truncate leading-tight">
                  {gestionnaire ? `${gestionnaire.firstName} ${gestionnaire.lastName}` : "Gestionnaire"}
                </p>
                <p className="text-white/40 text-[12.5px] truncate">{organization?.name}</p>
              </div>
            </div>
            <button
              onClick={deconnexion}
              className="flex items-center gap-3 px-3.5 py-2.5 text-white/50 text-[15px]"
            >
              <LogOut size={18} strokeWidth={1.75} /> Se déconnecter
            </button>
          </nav>
        )}
      </div>

      {/* ── Contenu ────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto pt-[52px] md:pt-0">
        {organization && !organization.canBook && (
          <div
            className="px-5 sm:px-8 lg:px-12 py-3.5"
            style={{ background: "#FFF8EC", borderBottom: "1px solid #F5E3C0" }}
          >
            <p className="text-[14px] text-[var(--org-ink)]">
              Votre compte n'est pas encore activé. Vous pouvez consulter votre
              espace, les réservations seront possibles une fois la convention signée.
            </p>
          </div>
        )}
        {rendu()}
      </main>
    </div>
  );
}
